import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

/**
 * Live countdown to an unlock timestamp. Ticks every 30s so it stays honest without churn.
 * Rounds UP, so "Opens in 4 days" always agrees with the date shown beside it.
 * `tone` picks the palette: 'dark' for dark surfaces, 'light' for white cards.
 */
export const UnlockCountdown = ({
  unlockDate,
  compact = false,
  tone = 'dark',
  className = '',
  testId,
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  if (!unlockDate) return null;

  const target = new Date(unlockDate).getTime();
  const ms = target - now;

  if (ms <= 0) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm ${tone === 'light' ? 'text-emerald-700' : 'text-emerald-300'} ${className}`}
        data-testid={testId}
      >
        <Clock className="w-3.5 h-3.5" />
        Open now
      </span>
    );
  }

  const mins = Math.ceil(ms / 60000);
  const hours = Math.ceil(ms / 3600000);
  const days = Math.ceil(ms / 86400000);

  let label;
  if (days > 1) label = `Opens in ${days} days`;
  else if (hours > 1) label = `Opens in ${hours} hours`;
  else label = `Opens in ${Math.max(1, mins)} min`;

  const when = new Date(target).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  if (compact) {
    const palette =
      tone === 'light'
        ? 'bg-amber-100 text-amber-900 border-amber-300'
        : 'bg-amber-500/15 text-amber-200 border-amber-500/25';
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border whitespace-nowrap ${palette} ${className}`}
        title={`Opens ${when}`}
        data-testid={testId}
      >
        <Clock className="w-3 h-3" />
        {label}
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col ${className}`} data-testid={testId}>
      <span
        className={`inline-flex items-center gap-1.5 text-sm ${tone === 'light' ? 'text-amber-900' : 'text-amber-200'}`}
      >
        <Clock className="w-3.5 h-3.5" />
        {label}
      </span>
      <span
        className={`text-xs mt-0.5 pl-5 ${tone === 'light' ? 'text-gray-600' : 'text-gray-400'}`}
      >
        {when}
      </span>
    </span>
  );
};

export default UnlockCountdown;
